import React, { useState } from "react";
import ConfirmModal from "./ConfirmModal";

const RoleList = ({
  roles,
  employees,
  setSelectedRole,
  setActiveView,
  deleteRole,
}) => {
  const [roleToDelete, setRoleToDelete] = useState(null);

  const handleEdit = (role) => {
    setSelectedRole(role);
    setActiveView("add-role");
  };

  const handleDeleteClick = (role) => {
    setRoleToDelete(role);
  };

  const handleConfirmDelete = () => {
    if (roleToDelete) {
      deleteRole(roleToDelete.id);
      setRoleToDelete(null);
    }
  };

  const getEmployeeCount = (roleId) => {
    return employees.filter((emp) => emp.roleId === roleId).length;
  };

  const handleViewEmployees = (roleId) => {
    const roleEmployees = employees.filter((emp) => emp.roleId === roleId);
    const role = roles.find((r) => r.id === roleId);

    if (roleEmployees.length === 0) {
      alert(`Role "${role.name}" currently has no employees.`);
      return;
    }

    alert(
      `Employees in role "${role.name}" (${roleEmployees.length}):\n\n` +
        roleEmployees
          .map((emp) => `• ${emp.firstName} ${emp.lastName} (${emp.email})`)
          .join("\n"),
    );
  };

  const employeeCount = roleToDelete ? getEmployeeCount(roleToDelete.id) : 0;
  const deleteModalMessage = roleToDelete
    ? employeeCount > 0
      ? (
          <>
            The role <strong>"{roleToDelete.name}"</strong> has {employeeCount} employee(s) assigned.
            You must reassign or remove them before deleting this role. Do you still want to continue?
          </>
        )
      : (
          <>
            Are you sure you want to delete the role <strong>"{roleToDelete.name}"</strong>? This
            action cannot be undone.
          </>
        )
    : "";

  return (
    <div className="roles-view">
      <ConfirmModal
        isOpen={!!roleToDelete}
        title="Delete role?"
        message={deleteModalMessage}
        confirmLabel="Delete role"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setRoleToDelete(null)}
      />
      <div className="list-header">
        <h2>
          <span>👔</span> Roles
        </h2>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <button
            className="add-btn"
            style={{ backgroundColor: "#2ecc71" }}
            onClick={() => {
              setSelectedRole(null);
              setActiveView("add-role");
            }}
          >
            <span>➕</span> Add role
          </button>
          <button
            className="add-btn"
            style={{ backgroundColor: "#3498db" }}
            onClick={() => setActiveView("employees")}
          >
            <span>👥</span> View Employees
          </button>
        </div>
      </div>

      {roles.length > 0 ? (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Role Name</th>
                  <th>Description</th>
                  <th>Department</th>
                  <th>Level</th>
                  <th>Employees</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => {
                  const employeeCount = getEmployeeCount(role.id);

                  return (
                    <tr key={role.id}>
                      <td>{role.id}</td>
                      <td>
                        <strong>{role.name}</strong>
                      </td>
                      <td>{role.description || "-"}</td>
                      <td>
                        <span
                          className="department-badge"
                          style={{
                            backgroundColor: getDepartmentColor(
                              role.department,
                            ),
                            color: "white",
                            padding: "0.3rem 0.8rem",
                            borderRadius: "20px",
                            fontSize: "0.9rem",
                          }}
                        >
                          {role.department}
                        </span>
                      </td>
                      <td>
                        <span
                          className="level-badge"
                          style={{
                            backgroundColor: getLevelColor(role.level),
                            color: "white",
                            padding: "0.3rem 0.8rem",
                            borderRadius: "20px",
                            fontSize: "0.9rem",
                          }}
                        >
                          {role.level}
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            cursor: employeeCount > 0 ? "pointer" : "default",
                          }}
                          onClick={() =>
                            employeeCount > 0 && handleViewEmployees(role.id)
                          }
                          title={
                            employeeCount > 0
                              ? "Click to view employees in this role"
                              : ""
                          }
                        >
                          <span
                            style={{
                              backgroundColor:
                                employeeCount > 0 ? "#2ecc71" : "#95a5a6",
                              color: "white",
                              padding: "0.3rem 0.8rem",
                              borderRadius: "20px",
                              fontWeight: "bold",
                              fontSize: "0.9rem",
                            }}
                          >
                            {employeeCount}
                          </span>
                          <span
                            style={{ fontSize: "0.9rem", color: "#7f8c8d" }}
                          >
                            person
                          </span>
                        </div>
                      </td>
                      <td className="action-cell">
                        <button
                          className="icon-btn edit-btn"
                          onClick={() => handleEdit(role)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        {employeeCount > 0 && (
                          <button
                            className="icon-btn"
                            style={{ color: "#2ecc71" }}
                            onClick={() => handleViewEmployees(role.id)}
                            title="View Employees"
                          >
                            👁️
                          </button>
                        )}
                        <button
                          className="icon-btn delete-btn"
                          onClick={() => handleDeleteClick(role)}
                          title="Delete role"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Statistics */}
          <div
            className="stats-section"
            style={{
              marginTop: "2rem",
              backgroundColor: "white",
              borderRadius: "10px",
              padding: "1.5rem",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <h3 style={{ marginBottom: "1rem", color: "#2c3e50" }}>
              <span>📈</span> Role Usage Statistics
            </h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "1rem",
                  borderRadius: "8px",
                  flex: "1",
                  minWidth: "200px",
                }}
              >
                <h4 style={{ marginBottom: "0.5rem", color: "#7f8c8d" }}>
                  Role Distribution
                </h4>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "2rem",
                        fontWeight: "bold",
                        color: "#3498db",
                      }}
                    >
                      {
                        roles.filter((role) => getEmployeeCount(role.id) > 0)
                          .length
                      }
                    </div>
                    <div style={{ fontSize: "0.9rem", color: "#95a5a6" }}>
                      used roles
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "2rem",
                        fontWeight: "bold",
                        color: "#95a5a6",
                      }}
                    >
                      {
                        roles.filter((role) => getEmployeeCount(role.id) === 0)
                          .length
                      }
                    </div>
                    <div style={{ fontSize: "0.9rem", color: "#95a5a6" }}>
                      empty roles
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "1rem",
                  borderRadius: "8px",
                  flex: "2",
                  minWidth: "300px",
                }}
              >
                <h4 style={{ marginBottom: "0.5rem", color: "#7f8c8d" }}>
                  Most Used Roles
                </h4>
                <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                  {(() => {
                    const roleUsage = roles
                      .map((role) => ({
                        name: role.name,
                        count: getEmployeeCount(role.id),
                        department: role.department,
                      }))
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 5);

                    return roleUsage.map((role) => (
                      <div
                        key={role.name}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.5rem 0",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        <div>
                          <div>
                            <strong>{role.name}</strong>
                          </div>
                          <div style={{ fontSize: "0.8rem", color: "#7f8c8d" }}>
                            {role.department}
                          </div>
                        </div>
                        <span
                          style={{
                            backgroundColor:
                              role.count > 0 ? "#2ecc71" : "#95a5a6",
                            color: "white",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "12px",
                            fontSize: "0.9rem",
                          }}
                        >
                          {role.count} employees
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="empty-message">
          <p>No roles yet. Click &quot;Add role&quot; to create one.</p>
        </div>
      )}
    </div>
  );
};

// Helper functions for department and level colors
const getDepartmentColor = (department) => {
  const colors = {
    IT: "#3498db",
    Management: "#2ecc71",
    Marketing: "#9b59b6",
    Sales: "#e67e22",
    Finance: "#e74c3c",
    "Human Resources": "#1abc9c",
    Security: "#f39c12",
    Other: "#95a5a6",
  };
  return colors[department] || "#95a5a6";
};

const getLevelColor = (level) => {
  const colors = {
    High: "#e74c3c",
    Medium: "#f39c12",
    Junior: "#2ecc71",
    Unknown: "#95a5a6",
  };
  return colors[level] || "#95a5a6";
};

export default RoleList;
